// Feature: M001-F075 — Web 2SV bypass banner (M001-D020 §7.1)
// NEW INVARIANT — ALLOCATION PENDING (define-feature-test): "every surface that
// displays 2SV override state displays the SAME state". See the MR description.
//
// THE SINGLE SOURCE OF TRUTH for "is the 2SV override (bypass) system on, and how
// many overrides are in effect".
//
// Before this existed, each surface asked a different endpoint and *inferred* the
// answer from an error:
//
//   * TwoSvBypassBanner  regex-matched the prose of the 403 from
//                        GET /v1/admin/2sv-overrides/count
//   * TwoSvOverridesPage echoed the raw error string from
//                        GET /v1/admin/2sv-overrides?active=true into a red box
//
// Two inferences, two endpoints, two request lifecycles — so they could and did
// contradict each other on the same screen. Nav said one thing, page said the
// other, and the operator had no way to tell which was true.
//
// Now Core answers the question directly (GET /v1/admin/2sv-overrides/status →
// `{ enabled, active_count }`, 200 whether the system is on or off), this provider
// makes that ONE request and holds ONE result, and every surface renders from it.
// Disagreement is not fixed here so much as made structurally impossible: there is
// only one value to render.
//
// Two rules this module exists to enforce:
//
//   1. NO STATE IS EVER DERIVED FROM SERVER PROSE. Only the explicit `enabled`
//      field, or — for failures — the HTTP status code. Changing the wording of a
//      backend error must not be able to change what an operator sees.
//   2. FAIL SAFE. Any outcome that is not an explicit `enabled: true` is reported
//      as disabled/permission/unavailable. No error path may ever produce
//      ENABLED, because a UI that claims "bypass active" (or hides the warning
//      entirely) on a failed request is worse than one that says it does not know.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { twoSvOverrideApi } from '../services/api';

// M004-F0668 (b) / T-M004-0124 (WebUI #21): the status is an admin warning, not
// live data. It was polled every 60 s and re-read on every auth-context identity
// change (72 GETs/hour from one idle Choreography tab). Now: one read at mount,
// a long background poll that skips hidden pages, navigation re-reads only when
// the last read is older than NAVIGATION_REREAD_MIN_AGE_MS, and REFRESH_EVENT
// (on demand) always reads.
export const POLL_INTERVAL_MS = 15 * 60_000;
export const NAVIGATION_REREAD_MIN_AGE_MS = 60_000;
export const REFRESH_EVENT = 'studioaeye:2sv-override-count-refresh';

export const OVERRIDE_STATE = {
  /** Core reported enabled: true. Only reachable from an explicit success body. */
  ENABLED: 'enabled',
  /** Core reported enabled: false. A valid, reportable system state. */
  DISABLED: 'disabled',
  /** Caller may not read the status (HTTP 403). */
  PERMISSION: 'permission',
  /** Status could not be determined: transport failure, 5xx, or a Core that
   *  predates the /status contract (404). Never treated as enabled. */
  UNAVAILABLE: 'unavailable',
};

/** Not-yet-known / not-applicable (non-admin). Renders nothing anywhere. */
export const UNKNOWN_STATUS = Object.freeze({ state: null, activeCount: 0 });

/**
 * Normalise a 200 status body. Pure — exported for direct unit assertions.
 * Only `enabled === true` yields ENABLED; anything else is DISABLED.
 */
export const deriveStatus = (payload) => {
  const body = payload?.data?.data ?? payload?.data ?? payload ?? {};
  const enabled = body.enabled === true;
  const rawCount = Number(body.active_count ?? body.activeCount ?? 0);
  const activeCount = Number.isFinite(rawCount) ? Math.max(0, rawCount) : 0;
  return {
    state: enabled ? OVERRIDE_STATE.ENABLED : OVERRIDE_STATE.DISABLED,
    // Nothing is in effect while the system is off, whatever rows exist.
    activeCount: enabled ? activeCount : 0,
  };
};

/**
 * Map a failed status read to a state. Pure — exported for direct unit assertions.
 *
 * Deliberately looks ONLY at the HTTP status code. It never reads
 * `error.response.data`, so no amount of rewording on the server can move the UI.
 * It can never return ENABLED or DISABLED: a failed read tells us nothing about
 * the config flag, and guessing is exactly the defect this replaces.
 */
export const deriveErrorState = (error) => {
  const status = error?.response?.status;
  // 401 => there is no authenticated session, so there is no admin to warn and
  // no state to report. Render nothing and let the auth layer deal with it.
  // This is not a fail-open: it cannot produce ENABLED either.
  if (status === 401) return null;
  if (status === 403) return OVERRIDE_STATE.PERMISSION;
  // 404 => this Core predates GET /status. Report "unavailable", never "disabled":
  // an old Core with the override system ON must not be shown as OFF.
  return OVERRIDE_STATE.UNAVAILABLE;
};

const TwoSvOverrideStatusContext = createContext(null);

export const TwoSvOverrideStatusProvider = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState(UNKNOWN_STATUS);

  // Fail safe on the audience question too: if we cannot establish that the
  // viewer is an admin, do not read or display override state at all.
  // Keyed on the boolean VALUE, so a new auth-context object with the same
  // answer is not a reason to read again (M004-F0668 (b)).
  const admin = typeof auth?.isAdmin === 'function' ? auth.isAdmin() === true : false;
  const adminRef = useRef(admin);
  adminRef.current = admin;
  const lastReadAtRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!adminRef.current) {
      setStatus(UNKNOWN_STATUS);
      return;
    }

    lastReadAtRef.current = Date.now();
    try {
      setStatus(deriveStatus(await twoSvOverrideApi.getStatus()));
    } catch (error) {
      setStatus({ state: deriveErrorState(error), activeCount: 0 });
      console.error('Failed to load 2SV override system status:', error);
    }
  }, []);

  useEffect(() => {
    refresh();

    if (!admin) {
      return undefined;
    }

    const poll = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    };
    const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
    window.addEventListener(REFRESH_EVENT, refresh);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(REFRESH_EVENT, refresh);
    };
  }, [refresh, admin]);

  // Navigating re-reads the status when the last read is not recent: it
  // shortens the window in which a stale value can be on screen, without a GET
  // per route change. It cannot cause a disagreement, because every surface
  // reads this one value either way.
  const firstNavigationRef = useRef(true);
  useEffect(() => {
    if (firstNavigationRef.current) { firstNavigationRef.current = false; return; }
    if (!adminRef.current) return;
    if (Date.now() - lastReadAtRef.current >= NAVIGATION_REREAD_MIN_AGE_MS) refresh();
  }, [refresh, location.pathname, location.search]);

  const value = useMemo(
    () => ({ state: status.state, activeCount: status.activeCount, refresh }),
    [status.state, status.activeCount, refresh],
  );

  return (
    <TwoSvOverrideStatusContext.Provider value={value}>
      {children}
    </TwoSvOverrideStatusContext.Provider>
  );
};

/**
 * Read the authoritative override status. Outside a provider this returns the
 * unknown status rather than throwing, so a surface can never fall back to its
 * own private inference — the failure mode is "render nothing", not "guess".
 */
export const useTwoSvOverrideStatus = () => (
  useContext(TwoSvOverrideStatusContext) || { ...UNKNOWN_STATUS, refresh: () => {} }
);

export default TwoSvOverrideStatusContext;
